import {
  DataBlockDictionaryColumnDto,
  DataBlockDictionaryItemDto,
  QueryFactory,
} from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useDictionaryById,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import {
  DXRG,
  useReactDataGrid,
  VirtualTable,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  sortingQueryParams,
  toScrollQueryParams,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { isEmpty } from '@lmt/unicorn-shared/app/components/measurements/copy-datablock-field/common';
import tableStyles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import styles from '@lmt/unicorn-shared/app/components/patients/list/PatientListPage.module.scss';
import {
  exportToExcelFile,
  createDictionaryItemObjects,
  createReferencedItemObjects,
  useDictionaryKeyTranslation,
  useInvalidateDependantsHandle,
  ExtendedColumn,
  checkIfColumnsAreTheSame,
  getUniqueColumnsWithDuplicateValues,
  removeEmptyFirstEntries,
  isColumnRequired,
} from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/helpers';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { removeTab } from '@lmt/unicorn-shared/app/features/navigation/operations';
import {
  FullHeightGridRoot,
  GetStyledRow,
  HideNullTitlesHeaderContent,
  HoverableTable,
  StickyHeaderRow,
  useConvertedSorting,
  useScrollingState,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import {
  readFileAsExcel,
  triggerDownloadFileDialog,
} from '@lmt/unicorn-shared/app/helpers/file-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { localizeDictionaryColumnTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import SettingsIcon from '@lmt/unicorn-shared/app/icons/gear.svg?react';
import PlusIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { getState, useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { Paper, useEventCallback } from '@mui/material';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { groupBy, isEqual } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StringParam, withDefault } from 'react-router-url-params';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';

import ArrowDown from '../../../icons/arrow-down.svg?react';
import { TableDotMenu } from '../../common/table/TableDotMenu';
import { useTabId } from '../../keep-alive/TabContext';

import pageStyles from './CustomDictionaryPage.module.scss';
import { useModal } from '../../common/ui-kit/modal/useModal';

export const CustomDictionaryPage: React.FC = React.memo(
  function CustomDictionary() {
    useReactDataGrid();
    const params = pages.customDictionary.useParams();
    const dictionaryId = params.dictionaryId!;
    const i18n = useScopedTranslation('custom_dictionary.list');

    const i18nDictKey = useDictionaryKeyTranslation();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const queryClient = useQueryClient();
    const globalForms = useGlobalForms();
    const tabId = useTabId();
    const navigate = useNavigate();
    const inputFileRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(false);

    const [queryParams, setQueryParams] = useQueryParamsInTab({
      search: withDefault(StringParam, undefined),
      ...sortingQueryParams(nameof<DataBlockDictionaryItemDto>('value'), false),
    });

    const { scrollParams, getRows, scrollToStart, pageSize } =
      useScrollingState();

    const { sorting, setSorting } = useConvertedSorting(
      queryParams,
      setQueryParams,
      scrollToStart,
    );

    const [isDictItemsQueryEnabled, setDictItemsQueryEnabled] =
      useState<boolean>(true);
    const dictionaryItemsQuery =
      QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
        {
          dictionaryId: dictionaryId,
          all: true,
          ...toScrollQueryParams({ ...queryParams, ...scrollParams }),
        },
        {
          //notifyOnChangeProps: ['data'],
          enabled: isDictItemsQueryEnabled,
          placeholderData: keepPreviousData,
        },
      );

    const currentDictionary = useDictionaryById(dictionaryId);

    const { invalidate: invalidateDependants } =
      useInvalidateDependantsHandle(dictionaryId);

    useEffect(
      function closePageIfDictionaryIsDeleted() {
        if (currentDictionary?.isDeleted) {
          removeTab(tabId!, true, navigate)(dispatch, getState);
        }
      },
      [currentDictionary?.isDeleted, tabId],
    );

    const invalidateQuery = useCallback(async () => {
      await queryClient.invalidateQueries({
        queryKey:
          QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(dictionaryId),
      });
      await invalidateDependants();
    }, [dictionaryId]);

    const onAddNewItem = useEventCallback(async () => {
      if (currentDictionary?.isReadOnly) {
        return;
      }
      globalForms.openCreateCustomDictionaryItem(dictionaryId);
    });
    useInsertKeyHandler(onAddNewItem);

    const isItemEditable = useCallback(
      (item: DataBlockDictionaryItemDto) => {
        return !(
          currentDictionary?.title === CustomDictionaryName.AbsenceReasons &&
          item.isDefault
        );
      },
      [currentDictionary],
    );

    useEffect(() => {
      if (currentDictionary?.manualOrdering) {
        setSorting([
          {
            columnName: nameof<DataBlockDictionaryItemDto>(
              'manualOrderPosition',
            ),
            direction: 'asc',
          },
        ]);
        onSearchUpdated('');
      }
    }, [currentDictionary?.manualOrdering]);

    const onEdit = useEventCallback(
      async (item: DataBlockDictionaryItemDto) => {
        if (currentDictionary?.isReadOnly) {
          return;
        }
        globalForms.openEditCustomDictionaryItem(dictionaryId, item);
      },
    );

    const onDelete = useEventCallback(
      async (item: DataBlockDictionaryItemDto) => {
        try {
          setIsLoading(true);
          await QueryFactory.DataBlockDictionaryItemClient.remove(item.id);
          await invalidateQuery();
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setIsLoading(false);
        }
      },
    );

    const onRestore = useEventCallback(
      async (item: DataBlockDictionaryItemDto) => {
        try {
          setIsLoading(true);
          await QueryFactory.DataBlockDictionaryItemClient.patch(item.id, {
            isDeleted: false,
          });
          await invalidateQuery();
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setIsLoading(false);
        }
      },
    );

    const [referencedData, setReferencedData] = useState<
      Record<string, DataBlockDictionaryItemDto[]>
    >({});

    const data = useMemo(
      () => (dictionaryItemsQuery.data?.data ?? []).filter((x) => !x.isDeleted),
      [dictionaryItemsQuery.data, referencedData, i18n.i18n.language],
    );

    const onReorder = useEventCallback(
      async (item: DataBlockDictionaryItemDto, direction: 'up' | 'down') => {
        const isUp = direction == 'up';
        const currentPos = item.manualOrderPosition;
        const otherItem = (isUp ? data.reverse() : data).find((i) =>
          isUp
            ? i.manualOrderPosition < currentPos
            : i.manualOrderPosition > currentPos,
        );
        if (!otherItem) {
          console.error('Tried to swap places with undefined!');
          throw Error('Tried to swap places with undefined!');
        }
        const newPos = otherItem.manualOrderPosition;
        await QueryFactory.DataBlockDictionaryItemClient.patch(item.id, {
          manualOrderPosition: newPos,
        });
        await QueryFactory.DataBlockDictionaryItemClient.patch(otherItem.id, {
          manualOrderPosition: currentPos,
        });
        await invalidateQuery();
      },
    );

    const columns = useMemo(() => {
      const columns: ExtendedColumn[] = [];

      if (currentDictionary?.manualOrdering) {
        columns.push({
          name: 'manualOrderPosition',
          getCellValue: (row: DataBlockDictionaryItemDto) => (
            <>
              {data.indexOf(row) > 0 ? (
                <IconButton
                  iconComponent={
                    <ArrowDown
                      className={clsx(
                        pageStyles.upsideDown,
                        pageStyles.arrowIcon,
                      )}
                    />
                  }
                  className={pageStyles.reorderButton}
                  onClick={() => onReorder(row, 'up')}
                  testId={'reorder-up'}
                />
              ) : null}
              {data.indexOf(row) != data.length - 1 ? (
                <IconButton
                  iconComponent={<ArrowDown className={pageStyles.arrowIcon} />}
                  className={pageStyles.reorderButton}
                  onClick={() => onReorder(row, 'down')}
                  testId={'reorder-down'}
                />
              ) : null}
            </>
          ),
        });
      }

      columns.push({
        name: 'value',
        title: i18n.t('columns.value'),
        getCellValue: (row: DataBlockDictionaryItemDto) =>
          i18nDictKey.t(row.value as any, { nsSeparator: false }),
      });

      (currentDictionary?.columns ?? [])
        .sort((a, b) =>
          isColumnRequired(a, currentDictionary)
            ? isColumnRequired(b, currentDictionary)
              ? a.title.localeCompare(b.title)
              : -1
            : 1,
        )
        .forEach((column) => {
          columns.push({
            name: column.title,
            title: localizeDictionaryColumnTitle(
              currentDictionary,
              column.title,
            ),
            dictionaryId: column.referenceDictionaryId,
            isUnique: column.isUnique,
            getCellValue: (row: DataBlockDictionaryItemDto) =>
              column.referenceDictionaryId
                ? i18nDictKey.t(
                    referencedData[column.title]?.find(
                      (x) => x.id === row.values[column.title],
                    )?.value as any,
                    {
                      nsSeparator: false,
                    },
                  )
                : i18nDictKey.t(row.values[column.title] as any, {
                    nsSeparator: false,
                  }),
          });
        });
      columns.push({
        name: 'option',
        title: '',
        getCellValue: (row) => (
          <TableDotMenu
            className={styles.optionsButton}
            options={[
              {
                title: i18n.t('options.edit'),
                onClick: () => onEdit(row),
                isHidden:
                  currentDictionary?.isReadOnly ||
                  row.isDeleted ||
                  !isItemEditable(row),
              },
              {
                title: i18n.t('options.restore'),
                onClick: () => onRestore(row),
                isHidden: currentDictionary?.isReadOnly || !row.isDeleted,
              },
              {
                title: i18n.t('options.delete'),
                onClick: () => onDelete(row),
                isDeleteItem: true,
                isUnderLine: true,
                isHidden: row.isDeleted,
              },
            ]}
          />
        ),
      });
      return columns;
    }, [currentDictionary, isItemEditable, data]);

    const tableColumnExtensions = useMemo<VirtualTable.ColumnExtension[]>(
      () => [
        { columnName: 'option', width: 76 },
        { columnName: 'manualOrderPosition', width: 76 },
      ],
      [],
    );

    const onSearchUpdated = useDebouncedCallback((value: string) => {
      scrollToStart();
      setQueryParams({
        search: value,
      });
    }, 300);

    const ValueFormatter = useCallback(
      (props: {
        row: DataBlockDictionaryItemDto;
        value: DataBlockDictionaryItemDto['value'];
      }) => {
        const { row, value } = props;
        return (
          <div
            data-deleted={true}
            className={clsx(tableStyles.valueCell, {
              [tableStyles.deleted]: row.isDeleted,
            })}
            title={value}
          >
            {value}
          </div>
        );
      },
      [],
    );

    const ColumnFormatter = useCallback(
      (props: any) => (
        <DXRG.DataTypeProvider
          for={['value']}
          formatterComponent={ValueFormatter}
          {...props}
        />
      ),
      [ValueFormatter],
    );
    const localizationMessages = useReactDataGridLocalization();

    const exportDictionary = useCallback(async () => {
      if (!currentDictionary) {
        return;
      }

      const exportColumns = [
        { header: i18n.t('columns.value'), key: 'value', width: 30 },
        ...currentDictionary.columns.map((x) => {
          return {
            header: localizeDictionaryColumnTitle(currentDictionary, x.title),
            key: x.title,
            width: 30,
          };
        }),
      ];
      const exportData = data.map((item) => {
        return {
          ...item,
          value: i18nDictKey.t(item.value as any, { nsSeparator: false }),
        };
      });
      const result = await exportToExcelFile(
        currentDictionary,
        exportData,
        referencedData,
        exportColumns,
      );
      await triggerDownloadFileDialog(
        new Blob([result]),
        `${i18nDictKey.t(currentDictionary.title as any)}.xlsx`,
      );
    }, [data, currentDictionary, referencedData]);

    const importDictionaryFromFile = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!currentDictionary) {
          return;
        }
        const file = e.target!.files![0];
        if (!file) {
          return;
        }

        try {
          setIsLoading(true);
          // #49024: prevent multiple refetching when importing; refetch only once when import is finished
          setDictItemsQueryEnabled(false);

          const rawValues = await readFileAsExcel(file);
          const values = removeEmptyFirstEntries(rawValues);

          const dataColumns = columns.filter(
            (x) => x.name !== 'manualOrderPosition' && x.name !== 'option',
          );

          if (!checkIfColumnsAreTheSame(values, dataColumns)) {
            await modals.showError({
              text: i18n.t('columns_are_not_the_same'),
            });
            return;
          }

          const columnsWithDuplicates = getUniqueColumnsWithDuplicateValues(
            values,
            dataColumns,
          );
          if (columnsWithDuplicates.length) {
            await modals.showError({
              text: i18n.t('unique_columns_have_duplicate_values', {
                columns: columnsWithDuplicates.join(', '),
              }),
            });
            return;
          }

          const referencedItems = await createReferencedItemObjects(
            values,
            dataColumns,
            referencedData,
          );
          const createdReferencedData: Record<
            string,
            DataBlockDictionaryItemDto[]
          > = {};
          for (const dictKey in referencedItems) {
            const createdItems: DataBlockDictionaryItemDto[] = [];
            const dictItems = referencedItems[dictKey];
            for (const item of dictItems) {
              try {
                const createdItem =
                  await QueryFactory.DataBlockDictionaryItemClient.create(item);
                createdItems.push(createdItem);
              } catch (e) {
                await modals.showError({
                  text: i18n.t('error_when_creating_referenced_item', {
                    value: item.value,
                    column: dictKey,
                    error: convertToErrorString(e),
                  }),
                });
                return;
              }
            }
            createdReferencedData[dictKey] = createdItems;
          }
          const allReferencedData: Record<
            string,
            DataBlockDictionaryItemDto[]
          > = {};
          for (const key in referencedData) {
            allReferencedData[key] = [
              ...referencedData[key],
              ...(createdReferencedData[key] ?? []),
            ];
          }

          const dictionaryItems = await createDictionaryItemObjects(
            currentDictionary.id,
            values,
            dataColumns,
            allReferencedData,
          );
          if (isEmpty(dictionaryItems)) {
            toast(i18n.t('no_data_to_import_toast'), {
              type: 'warning',
            });
            return;
          }

          const duplicateValues: Record<string, number> = {}; // key is item value, value is a number of occurrences
          const filteredDictionaryItems: DataBlockDictionaryItemDto[] = [];
          const itemGroups = groupBy(dictionaryItems, (item) =>
            item.value.toLowerCase(),
          );
          for (const key in itemGroups) {
            const items = itemGroups[key];
            if (items.length > 1) {
              duplicateValues[key] = items.length;
              // we import only last occurrence of item if file has duplicates
              filteredDictionaryItems.push(items[items.length - 1]);
              continue;
            }
            filteredDictionaryItems.push(items[0]);
          }

          const queryData = dictionaryItemsQuery.data?.data ?? [];
          const promises = filteredDictionaryItems.map((item) => {
            const existingItems = queryData.filter(
              (x) =>
                x.value.toLowerCase() === item.value.toLowerCase() ||
                (
                  i18nDictKey.t(x.value as any, {
                    nsSeparator: false,
                  }) as string
                ).toLowerCase() === item.value.toLowerCase(),
            );
            if (existingItems.length) {
              let existingItem = existingItems.find((x) => !x.isDeleted);
              if (!existingItem) {
                existingItem = existingItems[0];
              }
              // patch only if existing item is deleted or column values are different
              if (
                existingItem.isDeleted ||
                !isEqual(existingItem.values, item.values)
              ) {
                item.value = existingItem.value; // replace value with localization key if item is localized (to prevent loss of localization key)
                item.isDeleted = false;
                return QueryFactory.DataBlockDictionaryItemClient.patch(
                  existingItem.id,
                  item,
                );
              }
              return Promise.resolve();
            }
            return QueryFactory.DataBlockDictionaryItemClient.create(item);
          });
          await Promise.all(promises);
          if (Object.keys(duplicateValues).length) {
            await modals.showAlert({
              title: i18n.t('imported_file_contains_duplicates'),
              text: stringifyDuplicateValues(duplicateValues),
            });
          }
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setIsLoading(false);
          setDictItemsQueryEnabled(true);
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                dictionaryId,
              ),
          });
          // to allow choosing the same file again
          inputFileRef.current!.value = '';
        }
      },
      [currentDictionary, columns, referencedData, dictionaryItemsQuery.data],
    );

    const notSortableColumns = useMemo(() => {
      return (currentDictionary?.columns ?? []).map((x) => {
        return {
          columnName: x.title,
          sortingEnabled: false,
        };
      });
    }, sorting);

    return (
      <div
        className={styles.tableContainer}
        data-test-id="table-page-container"
      >
        {(currentDictionary?.columns ?? [])
          .filter((c) => c.referenceDictionaryId)
          .map((column) => (
            <ReferenceDictionaryColumnDataProvider
              key={column.id}
              column={column}
              onValuesReceived={(values) =>
                setReferencedData((prev) => ({
                  ...prev,
                  [column.title]: values,
                }))
              }
            />
          ))}

        <div className={styles.tableToolbar}>
          <TableTitle
            title={
              currentDictionary?.title
                ? i18nDictKey.t(currentDictionary.title as any)
                : ''
            }
            count={
              dictionaryItemsQuery.data?.data.filter((x) => !x.isDeleted)
                .length ?? undefined
            }
          />
          <div className={pageStyles.toolbar}>
            <div className={pageStyles.searchField}>
              {currentDictionary && !currentDictionary?.manualOrdering && (
                <SearchInput
                  placeholder={i18n.t('search_placeholder')}
                  bordered={true}
                  onSearchIconClick={onSearchUpdated}
                  onClear={onSearchUpdated}
                  onChange={(e) => onSearchUpdated(e.target.value)}
                  initialValue={queryParams.search}
                  allowAnySearch
                  blurOnEnter={false}
                />
              )}
            </div>
            {currentDictionary && !currentDictionary.isReadOnly && (
              <Button
                testId="plus-button"
                title={i18n.t('add_new_item', {
                  title: i18nDictKey.t(currentDictionary.title as any),
                })}
                iconComponent={<PlusIcon />}
                color={ButtonColor.Primary}
                onClick={onAddNewItem}
                className={pageStyles.createButton}
              />
            )}
          </div>
          {currentDictionary && !currentDictionary.isReadOnly && (
            <>
              <Button
                className={pageStyles.importButton}
                onClick={() => inputFileRef.current!.click()}
                title={i18n.t('import_button')}
                color={ButtonColor.Primary}
              />
              <input
                type="file"
                id="importExcelFile"
                ref={inputFileRef}
                className={'hidden'}
                onChange={importDictionaryFromFile}
              />
              <Button
                onClick={exportDictionary}
                title={i18n.t('export_button')}
                color={ButtonColor.Primary}
              />
            </>
          )}
          {currentDictionary &&
            !currentDictionary.isMandatory &&
            !currentDictionary.isReadOnly && (
              <IconButton
                testId="settings-icon"
                iconComponent={
                  <SettingsIcon className={pageStyles.settingsIcon} />
                }
                onClick={() =>
                  globalForms.openConfigureCustomDictionary(
                    currentDictionary.id,
                  )
                }
                className={pageStyles.settingsButton}
              />
            )}
        </div>
        <div className={styles.patientsTable}>
          <Loading
            loading={
              isLoading ||
              dictionaryItemsQuery.isLoading ||
              currentDictionary === undefined
            }
            title={i18n.t('loading')}
          >
            <Paper style={{ height: '100%', width: '100%' }}>
              <DXRG.Grid
                key={i18n.i18n.language}
                rows={data ?? []}
                columns={columns}
                getRowId={(row) => row.id}
                rootComponent={FullHeightGridRoot}
              >
                <ColumnFormatter />

                <DXRG.SearchState value={queryParams.search} />
                <DXRG.IntegratedFiltering
                  columnExtensions={[
                    { columnName: 'option', predicate: () => false },
                  ]}
                />
                <DXRG.SortingState
                  sorting={sorting}
                  onSortingChange={setSorting}
                  columnSortingEnabled={!currentDictionary?.manualOrdering}
                  columnExtensions={notSortableColumns}
                />
                {/* <DXRG.VirtualTableState
                  infiniteScrolling={false}
                  skip={scrollParams.skip}
                  totalRowCount={data.length ?? pageSize}
                  loading={dictionaryItemsQuery.isFetching}
                  getRows={getRows}
                  pageSize={pageSize}
                /> */}
                <DXRG.VirtualTable
                  messages={localizationMessages}
                  height={'auto'}
                  columnExtensions={tableColumnExtensions}
                  tableComponent={HoverableTable}
                  rowComponent={GetStyledRow(onEdit)}
                />

                <DXRG.TableHeaderRow
                  showSortingControls
                  contentComponent={HideNullTitlesHeaderContent}
                  rowComponent={StickyHeaderRow}
                />
              </DXRG.Grid>
            </Paper>
          </Loading>
        </div>
      </div>
    );
  },
);

export const ReferenceDictionaryColumnDataProvider: React.FC<{
  column: DataBlockDictionaryColumnDto;
  onValuesReceived: (data: DataBlockDictionaryItemDto[]) => void;
}> = (props) => {
  const column = props.column;
  const i18n = useTranslation(NS_DataBlockFields);
  const dictionaryQuery = QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
    { dictionaryId: column.referenceDictionaryId ?? 0, all: true },
    {
      enabled: !!column.referenceDictionaryId,
    },
  );

  useEffect(
    function callOnValuesReceivedWhenDataIsReceived() {
      if (dictionaryQuery.data) {
        props.onValuesReceived(
          dictionaryQuery.data.data
            .filter((x) => !x.isDeleted)
            .map((x) => ({
              id: x.id,
              value: i18n.t(x.value as any, { nsSeparator: false }),
              isDeleted: false,
              values: x.values,
              dictionaryId: x.dictionaryId,
              manualOrderPosition: x.manualOrderPosition,
            })),
        );
      }
    },
    [dictionaryQuery.data, column],
  );
  return null;
};

function stringifyDuplicateValues(duplicateValues: Record<string, number>) {
  const resultArray: string[] = [];
  for (const key in duplicateValues) {
    resultArray.push(`${key} (${duplicateValues[key]})`);
  }
  return resultArray.join(', ');
}
